import Cookies from 'js-cookie';
import { AnalyticsBrowser } from '@segment/analytics-next/dist/cjs/browser';
import 'singular-sdk';

// https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Local_storage
// https://gist.github.com/juliocesar/926500
// Needed because segment analytics.js will call getItem on localstorage and older Android webviews do not expose this

(function() {
  if (typeof window.localStorage === "undefined") {
    var data = {}, undef;
    window.localStorage = {
      setItem: function (id, val) {
        if (!data.hasOwnProperty(id)) {
          localStorage.length++;
        }
        return data[id] = String(val);
      },
      getItem: function (id) {
        return data.hasOwnProperty(id) ? data[id] : undef;
      },
      removeItem: function (id) {
        if (data.hasOwnProperty(id)) {
          localStorage.length--;
          return delete data[id];
        }
      },
      clear: function () {
        localStorage.length = 0;
        return data = {};
      },
      length: 0
    };
  }
})();

/* eslint-disable no-bitwise */
function generateUUID() {
  var uuid = [];
  var hexDigits = '0123456789abcdef';
  for (var i = 0; i < 36; i += 1) {
    uuid[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
  }
  uuid[14] = '4'; // bits 12-15 of the time_hi_and_version field to 0010
  uuid[19] = hexDigits.substr((uuid[19] & 0x3) | 0x8, 1); // bits 6-7 of the clock_seq_hi_and_reserved to 01
  uuid[8] = uuid[13] = uuid[18] = uuid[23] = '-'; // eslint-disable-line no-multi-assign
  return uuid.join('');
}
/* eslint-enable no-bitwise */

var ANONYMOUS_ID_COOKIE = 'aaid';
// eslint-disable-next-line no-bitwise
var MAX_EXPIRY = new Date(Math.pow( 2, 31 ) * 1000).toUTCString();

function getTLD(hostname) {
  var match = /dev\.doximity\.cloud|[-\w]+\.(?:[-\w]+\.xn--[-\w]+|[-\w]{3,}|[-\w]+\.[-\w]{2})$/i.exec(hostname);

  return match && match[0];
}

function getCookie(cookieName) {
  var match = document.cookie.match(new RegExp((cookieName + "=([^;]+)")));

  return match && match[1];
}

function setCookie(ref) {
  var name = ref.name;
  var value = ref.value; if ( value === void 0 ) value = '';
  var expires = ref.expires;
  var domain = ref.domain; if ( domain === void 0 ) domain = getTLD(window.location.hostname);
  var path = ref.path; if ( path === void 0 ) path = '/';

  // WARN: guard against coercing undefined values to String(undefined)
  expires = expires ? ("expires=" + expires) : '';
  domain = domain ? ("domain=" + domain) : '';
  document.cookie = name + "=" + value + ";path=" + path + ";" + expires + ";" + domain;
}

function setAnonymousIdCookie() {
  var anonymousId = getCookie(ANONYMOUS_ID_COOKIE);
  if (anonymousId) {
    return anonymousId;
  }

  anonymousId = generateUUID();
  var now = new Date();
  var inTwentyYears = new Date(Date.UTC(now.getUTCFullYear() + 20));

  setCookie({
    name: ANONYMOUS_ID_COOKIE,
    value: anonymousId,
    expires: inTwentyYears.toUTCString()
  });

  // temporary fix for ensuring segment uses the anonymousId we pass in
  setCookie({
    name: 'ajs_anonymous_id',
    value: anonymousId,
    expires: inTwentyYears.toUTCString()
  });

  return anonymousId;
}

var PLATFORMS = {
  WEB_IOS: 'web_ios',
  WEB_ANDROID: 'web_android',
  WEB_DESKTOP: 'web_desktop',
};

var isIPad = function (ua) { return !!ua.match(/ipad/i); };
var isIPod = function (ua) { return !!ua.match(/ipod/i); };
var isIPhone = function (ua) { return !!ua.match(/iphone/i); };
var isAmionIOSWebView = function (ua) { return !!ua.match(/AmionWebView/); };
/*
 * The Amion app isn't sending the right user agent, so for now, we'll also
 * match what it's sending out for web views, which looks like this:
 *
 *   Mozilla / 5.0(Linux; Android 6.0.1; SM - G935T Build / MMB29M; wv) AppleWebKit / 537.36(KHTML, like Gecko) Version / 4.0 Chrome / 54.0.2840.85 Mobile Safari / 537.36
 *
 * (where "wv" indicates it's a "web view")
 *
 * However, this breaks Facebook on Android which has a similar User - Agent
 * string, e.g.:
 *
 *   Mozilla / 5.0(Linux; Android 7.0; SM - G930R4 Build / NRD90M; wv) AppleWebKit / 537.36(KHTML, like Gecko) Version / 4.0 Chrome / 58.0.3029.83 Mobile Safari / 537.36[FB_IAB / FB4A; FBAV / 123.0.0.18.71;]
 *
 * So we exclude those user agents.
 */
var isAmionAndroidBrokenUa = function (ua) { return !!ua.match(/Android.*\swv\)/) && !ua.match(/\b(?:FB_IAB|FB4A|FBAV)\b/); };
var isAmionAndroid = function (ua) { return !!ua.match(/com\.doximity\.amiondroid/) || isAmionAndroidBrokenUa(ua); };
var isDialerIOSWebView = function (ua) { return !!ua.match(/DialerWebView/); };
var isDialerAndroid = function (ua) { return !!ua.match(/com\.doximity\.doxdialer/); };
var isDoximityIOSWebView = function (ua) { return !!ua.match(/DoximityWebView/); };
var isDoximityAndroid = function (ua) { return !!ua.match(/com\.doximity\.doximitydroid/); };
var isIOSApp = function (ua) { return isDoximityIOSWebView(ua) || isDialerIOSWebView(ua) || isAmionIOSWebView(ua); };
var isIOS = function (ua) { return isIPhone(ua) || isIPad(ua) || isIPod(ua) || isIOSApp(ua); };
var isAndroidApp = function (ua) { return isAmionAndroid(ua) || isDialerAndroid(ua) || isDoximityAndroid(ua); };
var isAndroid = function (ua) { return isAndroidApp(ua) || !!ua.match(/android/i); };

function getPlatform(ua) {
  if ( ua === void 0 ) ua = window.navigator.userAgent || '';

  if (isAndroid(ua)) { return PLATFORMS.WEB_ANDROID; }
  if (isIOS(ua)) { return PLATFORMS.WEB_IOS; }

  return PLATFORMS.WEB_DESKTOP;
}

var CAMPAIGN_COOKIE = 'dox_analytics_campaign';

function safeObjectDecode(value) {
  try {
    return JSON.parse(atob(value));
  } catch (e) {
    return null;
  }
}

var CookieJar = function CookieJar() {
  this.decodeCookieObject = safeObjectDecode;
};

var prototypeAccessors = { get: { configurable: true } };

prototypeAccessors.get.get = function () {
  return this.getCookieValueProxy;
};

CookieJar.prototype.set = function set (name, value, options) {
    if ( options === void 0 ) options = {};

  var castValue = typeof value === 'object' ? btoa(JSON.stringify(value)) : value;

  var secureOptions = Object.assign({}, options);

  // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Set-Cookie/SameSite
  if (!secureOptions.sameSite) {
    secureOptions.sameSite = 'Lax';
  }

  // set Secure=true when sameSite is "None"
  // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Set-Cookie/SameSite
  if (secureOptions.sameSite === 'None') {
    secureOptions.secure = true;
  }

  Cookies.set(name, castValue, secureOptions);
};

CookieJar.prototype.purge = function purge () {
  /* eslint-disable prefer-rest-params */
  Cookies.call('remove', arguments);
  Cookies.remove(arguments[0], { path: '' });
  Cookies.remove(arguments[0], { path: '/' });
  /* eslint-enable prefer-rest-params */
};

CookieJar.prototype.getCookieValueProxy = function getCookieValueProxy (v) {
  var cookies = Cookies.get();

  switch (v) {
    case CAMPAIGN_COOKIE:
      return safeObjectDecode(cookies[CAMPAIGN_COOKIE]);
    default:
      if (v) {
        return cookies[v];
      }

      return cookies;
  }
};

CookieJar.prototype.encodeCookieObject = function encodeCookieObject (cookieValue) {
  try {
    return btoa(JSON.stringify(cookieValue));
  } catch (e) {
    return null;
  }
};

Object.defineProperties( CookieJar.prototype, prototypeAccessors );

var cookieJar = new CookieJar();

/**
 * Creates a RegExp object for matching direct referrer domains.
 *
 * @param {(string|RegExp|Array<string|RegExp>)} directReferrerDomain - Domain(s) to be considered as direct referrers.
 * @returns {RegExp} - A RegExp that will match any of the specified domains.
 */
function createDirectReferrerRE(directReferrerDomain) {
  // Default Doximity domain pattern if no directReferrerDomain is provided
  if (!directReferrerDomain) {
    return /^(\w*\.)?doximity\.com$/;
  }

  var domains = Array.isArray(directReferrerDomain) ? directReferrerDomain : [directReferrerDomain];

  // Convert each domain to a string pattern
  var patterns = domains.map(function (domain) {
    if (domain instanceof RegExp) {
      // Remove the surrounding slashes and any flags
      return domain.toString().replace(/^\/|\/[gimuy]*$/g, '');
    }

    // For string domains, escape special regex characters and match the domain exactly
    return domain.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  });

  // Join patterns with OR operator and create new RegExp
  return new RegExp(("^(" + (patterns.join('|')) + ")$"));
}

var HOST_REGEXP = new RegExp(/^https?:\/\/([^/?#:]+)(?:[/?#:]|$)/i);

var DoxReferrer = function DoxReferrer(url, ref) {
  if ( url === void 0 ) url = '';
  if ( ref === void 0 ) ref = {};
  var isDirectRE = ref.isDirectRE;
  var isEdaRE = ref.isEdaRE;
  var host = ref.host;
  var directReferrerDomain = ref.directReferrerDomain;

  this.url = url;
  this.internalSource = null;
  this.isDirectRE = isDirectRE || createDirectReferrerRE(directReferrerDomain);
  this.isEdaRE = isEdaRE || /^email-delivery\.doximity\.com$/;
  this.host = host || window.location.host;
};

var prototypeAccessors$1 = { isEdaReferral: { configurable: true },isDirectReferral: { configurable: true },isExternalReferral: { configurable: true },source: { configurable: true } };

prototypeAccessors$1.isEdaReferral.get = function () {
  return this.isEdaRE.test(this.source);
};

prototypeAccessors$1.isDirectReferral.get = function () {
  return this.source === '' || this.isDirectRE.test(this.source) || this.source === this.host;
};

prototypeAccessors$1.isExternalReferral.get = function () {
  return !this.isDirectReferral;
};

prototypeAccessors$1.source.get = function () {
  this.internalSource = this.internalSource || this.getOrigin(this.url);

  return this.internalSource;
};

DoxReferrer.prototype.getOrigin = function getOrigin (str) {
  try {
    return str.match(HOST_REGEXP).pop();
  } catch (e) {
    return '';
  }
};

Object.defineProperties( DoxReferrer.prototype, prototypeAccessors$1 );

function extractQueryParams() {
  var query = {};

  document
    .location
    .search
    .substr(1)
    .split('&')
    .forEach(function (key_value) {
      var pair = key_value.split('=');
      if (pair[0] && pair[1]) {
        query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
      }
    });

  return query;
}

/* eslint-disable no-underscore-dangle */
var EDA_TRACKABLE_PARAM = '_eda_link_uuid';
var UTM_SOURCE_DOXIMITY_EDA = 'doximity-eda';
var UTM_MEDIUM_EMAIL = 'email';
var UTM_MEDIUM_REFERRAL = 'referral';
var UTM_MEDIUM_DIRECT = 'direct';

var DoxCampaignFromBrowser = function DoxCampaignFromBrowser(ref) {
  if ( ref === void 0 ) ref = {};
  var injectDocument = ref.injectDocument;
  var injectQueryParams = ref.injectQueryParams;
  var injectCookieJar = ref.injectCookieJar;
  var directReferrerDomain = ref.directReferrerDomain;

  this.initialized = false;
  this.document = injectDocument || document;
  this.queryParams = injectQueryParams || extractQueryParams();
  this.cookieJar = injectCookieJar || cookieJar;
  this.referrer = new DoxReferrer(this.document.referrer, { directReferrerDomain: directReferrerDomain });
};

var prototypeAccessors$2 = { toHash: { configurable: true },name: { configurable: true },source: { configurable: true },medium: { configurable: true },content: { configurable: true },uuid: { configurable: true },uuidFromEDAAnalyticsContinuationCampaign: { configurable: true },_isEdaCampaign: { configurable: true } };

prototypeAccessors$2.toHash.get = function () {
    var this$1 = this;

  var props = {
    medium: this.medium
  };

  // Add the other props to our hash if they exist
  ['name', 'source', 'content', 'uuid'].forEach(function (campaignProp) {
    var temp = this$1[campaignProp];
    if (temp) {
      props[campaignProp] = temp;
    }
  });

  return props;
};

prototypeAccessors$2.name.get = function () {
  return this.queryParams.utm_campaign || null;
};

prototypeAccessors$2.source.get = function () {
  if (this.queryParams.utm_source) {
    return this.queryParams.utm_source;
  }

  if (!this.queryParams.utm_medium && this._isEdaCampaign) {
    return UTM_SOURCE_DOXIMITY_EDA;
  }

  if (!this.queryParams.utm_medium && this.referrer.isExternalReferral) {
    return this.referrer.source;
  }

  return null;
};

prototypeAccessors$2.medium.get = function () {
  if (this.queryParams.utm_medium) {
    return this.queryParams.utm_medium;
  }

  if (this._isEdaCampaign) {
    return UTM_MEDIUM_EMAIL;
  }

  if (this.referrer.isExternalReferral) {
    return UTM_MEDIUM_REFERRAL;
  }

  return UTM_MEDIUM_DIRECT;
};

prototypeAccessors$2.content.get = function () {
  return this.queryParams.utm_content || null;
};

prototypeAccessors$2.uuid.get = function () {
  if (this._isEdaCampaign) {
    return (
      this.content || // prefer utm_content which should hold the message.UUID
      this.queryParams[EDA_TRACKABLE_PARAM] || // otherwise fall back to _eda_link_uuid
      this.uuidFromEDAAnalyticsContinuationCampaign // otherwise fall back to uuid stored in a cookie
    );
  }

  return null;
};

/*
- When a user visits with EDA UTM params, we track the campaign using the UUID parameter
- If the page is refreshed and UTM params are still present, we maintain continuity
  with the previous campaign from the campaign cookie
*/
prototypeAccessors$2.uuidFromEDAAnalyticsContinuationCampaign.get = function () {
  var preExistingCampaign = this.cookieJar.get(CAMPAIGN_COOKIE);

  if (!preExistingCampaign) { return null; }
  if (this.queryParams.utm_campaign !== preExistingCampaign.name) { return null; }
  if (this.queryParams.utm_source !== preExistingCampaign.source) { return null; }
  if (this.queryParams.utm_medium !== preExistingCampaign.medium) { return null; }

  return preExistingCampaign.uuid || null;
};

prototypeAccessors$2._isEdaCampaign.get = function () {
  if (this.referrer.isEdaReferral) { return true; }

  return (
    this.queryParams.utm_source === UTM_SOURCE_DOXIMITY_EDA ||
    this.queryParams.utm_source === 'eda' || // legacy term
    this.queryParams.utm_source === 'dox-eda' // legacy term
  );
};

Object.defineProperties( DoxCampaignFromBrowser.prototype, prototypeAccessors$2 );

/* eslint-enable no-underscore-dangle */

function atMidnightOrInOneHour(time) {
  if ( time === void 0 ) time = new Date();

  var midnight = new Date(Date.UTC(time.getUTCFullYear(), time.getUTCMonth(), time.getUTCDate() + 1));

  time.setUTCHours(time.getUTCHours() + 1);

  return (time < midnight ? time : midnight).toUTCString();
}

function updateCampaignCookie(campaignProperties) {
  cookieJar.set(CAMPAIGN_COOKIE, campaignProperties, {
    expires: new Date(atMidnightOrInOneHour())
  });

  return campaignProperties;
}

function getActiveCampaign() {
  return cookieJar.get(CAMPAIGN_COOKIE);
}

function detectInitialBrowserCampaign(directReferrerDomain) {
  return new DoxCampaignFromBrowser({ directReferrerDomain: directReferrerDomain }).toHash;
}

function trackCampaignInitiated(analytics, initiallyDetectedCampaign) {
  var event = {
    name: 'campaign_identification'
  };

  Object.keys(initiallyDetectedCampaign).forEach(function (key) {
    event[("identified_campaign_" + key)] = initiallyDetectedCampaign[key];
  });

  analytics.track({
    event: event,
    action: 'completed',
    product: 'uncategorized'
  });
}

function setActiveCampaign(directReferrerDomain) {
  var detectedCampaign = detectInitialBrowserCampaign(directReferrerDomain);
  var previousCampaign = cookieJar.get(CAMPAIGN_COOKIE);

  if (previousCampaign && detectedCampaign.medium === 'direct') {
    return updateCampaignCookie(previousCampaign);
  }

  return updateCampaignCookie(detectedCampaign);
}

function getCampaign(directReferrerDomain) {
  var campaign = getActiveCampaign();
  if (campaign) { return campaign; }

  setActiveCampaign(directReferrerDomain);
  return getActiveCampaign();
}

var TOP = 'top';
var BOTTOM = 'bottom';
var LEFT = 'left';
var RIGHT = 'right';

function opposite(direction) {
  switch (direction) {
    case TOP:
      return BOTTOM;
    case BOTTOM:
      return TOP;
    case LEFT:
      return RIGHT;
    case RIGHT:
      return LEFT;
    default:
      throw new Error(("Unknown direction: " + direction));
  }
}

function parentContains(parentRect, rect, direction) {
  switch (direction) {
    case TOP:
    case LEFT:
      return (
        rect[direction] >= parentRect[direction] &&
        rect[direction] < parentRect[opposite(direction)]
      );
    case BOTTOM:
    case RIGHT:
      return (
        rect[direction] <= parentRect[direction] &&
        rect[direction] > parentRect[opposite(direction)]
      );
    default:
      throw new Error(("Unknown direction: " + direction));
  }
}

function isVisible(element, x, y) {
  if (
    !element ||
    !(element instanceof Element) ||
    getComputedStyle(element).visibility === 'hidden' ||
    getComputedStyle(element).display === 'none' ||
    !element.getBoundingClientRect ||
    !element.parentNode
  )
    { return false; }

  var rect = element.getBoundingClientRect();
  var parentElement = element.parentNode;

  var document_documentElement = document.documentElement;
  var documentTop = document_documentElement.clientTop;
  var documentHeight = document_documentElement.clientHeight;
  var documentLeft = document_documentElement.clientLeft;
  var documentWidth = document_documentElement.clientWidth;

  var documentRect = {
    top: documentTop,
    bottom: documentHeight - documentTop,
    left: documentLeft,
    right: documentWidth - documentLeft,
  };

  if (
    !parentContains(documentRect, rect, x) ||
    !parentContains(documentRect, rect, y)
  )
    { return false; }

  // recursively check up DOM tree to check for scrollable containers
  while (parentElement !== document.documentElement) {
    if (
      !parentElement ||
      !(parentElement instanceof Element) ||
      getComputedStyle(parentElement).visibility === 'hidden' ||
      getComputedStyle(parentElement).display === 'none'
    )
      { return false; }

    var isHiddenX = !!getComputedStyle(parentElement)['overflow-x'].match(
      /hidden|scroll/
    );
    var isHiddenY = !!getComputedStyle(parentElement)['overflow-y'].match(
      /hidden|scroll/
    );
    var parentRect = parentElement.getBoundingClientRect();

    if (
      (isHiddenX && !parentContains(parentRect, rect, x)) ||
      (isHiddenY && !parentContains(parentRect, rect, y))
    )
      { return false; }

    parentElement = parentElement.parentNode;
  }

  return true;
}

function checkShown(impressionHandlers) {
  if ( impressionHandlers === void 0 ) impressionHandlers = [];

  impressionHandlers.forEach(
    function (ref, index, handlers) {
      var element = ref.element;
      var x = ref.x;
      var y = ref.y;
      var handler = ref.handler;
      var isShowing = ref.isShowing;

      if (isVisible(element, x, y)) {
        if (!isShowing) {
          handler();
          handlers[index].isShowing = true;
        }
      } else {
        handlers[index].isShowing = false;
      }
    }
  );
}

var noop = function () {};

var computeProperties = function (properties, element, event) {
    if ( properties === void 0 ) properties = {};

    return typeof properties === 'function' ? properties(element, event) : properties;
};

function Impression() {
  var args = [], len = arguments.length;
  while ( len-- ) args[ len ] = arguments[ len ];

  var state = {};
  var listeners = {};

  var registerShownHandler = function registerShownHandler(element, ref, handler) {
    var x = ref.x;
    var y = ref.y;

    state.shownRegistry[this.id].push({
      element: element,
      x: x,
      y: y,
      handler: handler
    });
  };

  var isShowing = function isShowing(element, ref) {
    var x = ref.x;
    var y = ref.y;

    return !!(
      state.shownRegistry[this.id] &&
      state.shownRegistry[this.id].find(
        function (handler) { return handler.element === element && handler.isShowing && handler.x === x && handler.y === y; }
      )
    );
  };

  var _Impression = function _Impression(ref) {
    if ( ref === void 0 ) ref = {};
    var id = ref.id; if ( id === void 0 ) id = generateUUID();
    var onTrack = ref.onTrack; if ( onTrack === void 0 ) onTrack = noop;
    var shownRegistry = ref.shownRegistry; if ( shownRegistry === void 0 ) shownRegistry = {};

    this.id = id;
    this.onTrack = onTrack;

    state.shownRegistry = shownRegistry;
    state.shownRegistry[this.id] = [];

    registerShownHandler = registerShownHandler.bind(this);
    isShowing = isShowing.bind(this);
  };

  _Impression.prototype.unregister = function unregister (removeListeners) {
      if ( removeListeners === void 0 ) removeListeners = true;

    delete state.shownRegistry[this.id];

    if (removeListeners) {
      Object.keys(listeners).forEach(function (id) {
        var ref = listeners[id];
          var element = ref.element;
          var type = ref.type;
          var handler = ref.handler;
        if (element) { element.removeEventListener(type, handler); }
        delete listeners[id];
      });
    }
  };

  _Impression.prototype.refreshId = function refreshId () {
    var id = generateUUID();

    // reset shown elements
    state.shownRegistry[id] = state.shownRegistry[this.id];
    state.shownRegistry[id].forEach(function (handler) {
      handler.isShowing = false;
    });

    this.unregister(false);

    this.id = id;
  };

  _Impression.prototype.trackShown = function trackShown (name, element, properties, options, onValidation) {
      var this$1 = this;
      if ( properties === void 0 ) properties = {};
      if ( options === void 0 ) options = {};

    // Support trackShown(element, properties) signature with data-event
    if (name instanceof Element) {
      properties = element;
      element = name;
      name = element.dataset.event;
      if (!name) { throw new Error('No event name specified for trackShown'); }
    }

    if (!(element instanceof Element)) { throw new Error('trackShown must be provided a DOM element to track.'); }

    var onShown = options.onShown; if ( onShown === void 0 ) onShown = noop;
      var context = options.context;
    if (typeof onShown !== 'function') { throw new Error('onShown must be a function.'); }

    registerShownHandler(element, { x: LEFT, y: TOP }, function () {
      this$1.track(
        {
          event: Object.assign({}, {name: name}, computeProperties(properties, element)),
          action: 'shown',
          context: context
        },
        onValidation
      );

      onShown();
    });
  };

  _Impression.prototype.trackShownFully = function trackShownFully (name, element, properties, options, onValidation) {
      var this$1 = this;
      if ( properties === void 0 ) properties = {};
      if ( options === void 0 ) options = {};

    // Support trackShownFully(element, properties) signature with data-event
    if (name instanceof Element) {
      properties = element;
      element = name;
      name = element.dataset.event;
      if (!name) { throw new Error('No event name specified for trackShownFully'); }
    }

    if (!(element instanceof Element)) { throw new Error('trackShownFully must be provided a DOM element to track.'); }

    var onShownFully = options.onShownFully; if ( onShownFully === void 0 ) onShownFully = noop;
      var context = options.context;
    if (typeof onShownFully !== 'function') { throw new Error('onShownFully must be a function.'); }

    this.trackShown(name, element, properties, options, onValidation);

    registerShownHandler(element, { x: RIGHT, y: BOTTOM }, function () {
      this$1.track(
        {
          event: Object.assign({}, {name: name}, computeProperties(properties, element)),
          action: 'shown_fully',
          context: context
        },
        onValidation
      );

      onShownFully();
    });
  };

  _Impression.prototype.trackTapped = function trackTapped (name, element, properties, options, onValidation) {
      var this$1 = this;
      if ( properties === void 0 ) properties = {};
      if ( options === void 0 ) options = {};

    // Support trackTapped(element, properties, options) signature with data-event
    if (name instanceof Element) {
      options = properties;
      properties = element;
      element = name;
      name = element.dataset.event;
      if (!name) { throw new Error('No event name specified for trackTapped'); }
    }

    if (!(element instanceof Element)) { throw new Error('trackTapped must be provided a DOM element to track.'); }

    var trackShownFully = options.trackShownFully;
      var onTapped = options.onTapped; if ( onTapped === void 0 ) onTapped = noop;
      var onShown = options.onShown; if ( onShown === void 0 ) onShown = noop;
      var onShownFully = options.onShownFully; if ( onShownFully === void 0 ) onShownFully = noop;
      var registeredEventId = options.eventId;
      var context = options.context;

    var callbacks = { onTapped: onTapped, onShown: onShown, onShownFully: onShownFully };
    Object.keys(callbacks).forEach(function (fn) {
      if (typeof callbacks[fn] !== 'function') { throw new Error((fn + " must be a function.")); }
    });

    // if registeredEventId is present, then trackTapped is being called again on an
    // existing event, which means we don't want to fire the shown event again
    if (!registeredEventId) {
      if (trackShownFully) {
        this.trackShownFully(name, element, properties, options, onValidation);
      } else {
        this.trackShown(name, element, properties, options, onValidation);
      }
    }

    var handler = function (event) {
      if (!isShowing(element, { x: LEFT, y: TOP })) {
        this$1.track(
          {
            event: Object.assign({}, {name: name},
              computeProperties(properties, element, event)),
            action: 'shown',
            context: context
          },
          onValidation
        );
        onShown();
      }

      this$1.track(
        {
          event: Object.assign({}, {name: name}, computeProperties(properties, element, event)),
          action: 'tapped',
          context: context
        },
        onValidation
      );
      onTapped();
    };

    var type = 'click';

    // if registeredEventId is present, that means a listener already exists
    // and should be removed so we don't stack up and fire many events
    if (registeredEventId) { this.removeEvent(registeredEventId); }

    element.addEventListener(type, handler);
    var eventId = generateUUID();
    listeners[eventId] = { name: name, element: element, type: type, handler: handler };
    return eventId;
  };

  _Impression.prototype.trackEntered = function trackEntered (name, element, properties, options, onValidation) {
      var this$1 = this;
      if ( properties === void 0 ) properties = {};
      if ( options === void 0 ) options = {};

    // Support trackEntered(element, properties, options) signature with data-event
    if (name instanceof Element) {
      options = properties;
      properties = element;
      element = name;
      name = element.dataset.event;
      if (!name) { throw new Error('No event name specified for trackEntered'); }
    }

    if (!(element instanceof Element)) { throw new Error('trackEntered must be provided a DOM element to track.'); }

    var trackShownFully = options.trackShownFully;
      var onEntered = options.onEntered; if ( onEntered === void 0 ) onEntered = noop;
      var onShown = options.onShown; if ( onShown === void 0 ) onShown = noop;
      var onShownFully = options.onShownFully; if ( onShownFully === void 0 ) onShownFully = noop;
      var registeredEventId = options.eventId;
      var context = options.context;

    var callbacks = { onEntered: onEntered, onShown: onShown, onShownFully: onShownFully };
    Object.keys(callbacks).forEach(function (fn) {
      if (typeof callbacks[fn] !== 'function') { throw new Error((fn + " must be a function.")); }
    });

    // if registeredEventId is present, then trackTapped is being called again on an
    // existing event, which means we don't want to fire the shown event again
    if (!registeredEventId) {
      if (trackShownFully) {
        this.trackShownFully(name, element, properties, options, onValidation);
      } else {
        this.trackShown(name, element, properties, options, onValidation);
      }
    }

    var handler = function (event) {
      if (!isShowing(element, { x: LEFT, y: TOP })) {
        this$1.track(
          {
            event: Object.assign({}, {name: name},
              computeProperties(properties, element, event)),
            action: 'shown',
            context: context
          },
          onValidation
        );
        onShown();
      }

      this$1.track(
        {
          event: Object.assign({}, {name: name},
            computeProperties(properties, element, event)),
          action: {
            name: 'entered',
            input: element.value
          },
          context: context
        },
        onValidation
      );
      onEntered();
    };

    var type = 'input';

    // if registeredEventId is present, that means a listener already exists
    // and should be removed so we don't stack up and fire many events
    if (registeredEventId) { this.removeEvent(registeredEventId); }

    element.addEventListener(type, handler);
    var eventId = generateUUID();
    listeners[eventId] = { name: name, element: element, type: type, handler: handler };
    return eventId;
  };

  _Impression.prototype.track = function track (message, onValidation) {
      if ( onValidation === void 0 ) onValidation = noop;

    message = Object.assign({}, {impression_id: this.id}, message);

    this.onTrack(message, onValidation);
  };

  _Impression.prototype.removeEvent = function removeEvent (eventId) {
    var listener = listeners[eventId];
    var element = listener.element;
      var type = listener.type;

    // destructuring a function invokes it, so just get a reference to it
    var handler = listener.handler; // eslint-disable-line prefer-destructuring

    element.removeEventListener(type, handler);
    delete listeners[eventId];
  };

  return new (Function.prototype.bind.apply( _Impression, [ null ].concat( args) ));
}

function Section() {
  var args = [], len = arguments.length;
  while ( len-- ) args[ len ] = arguments[ len ];

  var state = { impressions: [] };

  var _Section = function _Section(ref) {
    if ( ref === void 0 ) ref = {};
    var id = ref.id; if ( id === void 0 ) id = generateUUID();
    var onTrack = ref.onTrack; if ( onTrack === void 0 ) onTrack = noop;
    var product = ref.product;
    var shownRegistry = ref.shownRegistry;

    this.id = id;
    this.onTrack = onTrack;

    state.product = product;
    state.shownRegistry = shownRegistry;

    this.track = this.track.bind(this);
  };

  var prototypeAccessors = { impressions: { configurable: true },product: { configurable: true } };

  prototypeAccessors.impressions.get = function () {
    return state.impressions;
  };

  prototypeAccessors.product.get = function () {
    return state.product;
  };

  _Section.prototype.refreshId = function refreshId () {
    this.id = generateUUID();
    this.impressions.forEach(function (impression) { return impression.refreshId(); });
  };

  _Section.prototype.registerImpression = function registerImpression () {
    var impression = new Impression({
      onTrack: this.track,
      shownRegistry: state.shownRegistry
    });
    this.impressions.push(impression);

    return impression;
  };

  _Section.prototype.unregisterImpression = function unregisterImpression (impressionId, ref) {
      var this$1 = this;
      if ( ref === void 0 ) ref = {};
      var delay = ref.delay;

    var cleanupFn = function () {
      state.impressions.forEach(function (impression) {
        if (impression.id === impressionId) {
          impression.unregister();
        }
      });

      state.impressions = this$1.impressions.filter(function (impression) { return impression.id !== impressionId; });
    };

    if (delay) {
      setTimeout(cleanupFn, delay);
    } else {
      cleanupFn();
    }
  };

  _Section.prototype.track = function track (message, options, callback) {
      if ( options === void 0 ) options = {};

    if (typeof options === 'function') {
      callback = options;
      options = {};
    }

    if (options.refresh) { this.refreshId(); }

    // auto-fill product
    if (!message.product) {
      message.product = this.product;
    }

    message = Object.assign({}, {section_id: this.id},
      message);

    this.onTrack(message, callback);
  };

  Object.defineProperties( _Section.prototype, prototypeAccessors );

  return new (Function.prototype.bind.apply( _Section, [ null ].concat( args) ));
}

var DEBUG_COOKIE = 'dox_analytics_debug';
var PREFIX = '[DoxAnalytics]';

var getDebugCookie = function () {
  try {
    return JSON.parse(getCookie(DEBUG_COOKIE));
  } catch (e) {
    return false;
  }
};

/* eslint-disable no-console */
function Logger() {
  var state = {
    isPaused: false,
    debug: getDebugCookie(),
    groupLevel: 0,
  };

  var _Logger = function _Logger () {};

  var prototypeAccessors = { debugMode: { configurable: true } };

  _Logger.prototype.log = function log () {
      var args = [], len = arguments.length;
      while ( len-- ) args[ len ] = arguments[ len ];

    if (console && console.log) {
      console.log.apply(console, args);
    }
  };

  _Logger.prototype.warn = function warn () {
      var args = [], len = arguments.length;
      while ( len-- ) args[ len ] = arguments[ len ];

    if (console && console.warn) {
      console.warn.apply(console, args);
    }
  };

  _Logger.prototype.table = function table () {
      var args = [], len = arguments.length;
      while ( len-- ) args[ len ] = arguments[ len ];

    if (console && console.table) {
      console.table.apply(console, args);
    }
  };

  _Logger.prototype.json = function json (object) {
    try {
      this.log(JSON.stringify(object, null, 2));
    } catch (e) {
      this.warn(("Could not stringify: " + object));
    }
  };

  _Logger.prototype.debug = function debug (options, message) {
      var args = [], len = arguments.length - 2;
      while ( len-- > 0 ) args[ len ] = arguments[ len + 2 ];

    if (typeof options === 'string') {
      args = message ? [message ].concat( args) : args;
      message = options;
      options = {};
    }

    if (state.groupLevel === 0) {
      message = PREFIX + " " + message;
    }

    if (!state.isPaused && state.debug) {
      if (options.json) {
        this.json(message);
      } else {
        var log = options.warn ? this.warn : this.log;
        log.apply(
          void 0, [ ("" + message + ((args.length && !options.warn) || options.table ? ':' : '.')) ].concat( args )
        );
      }
    }

    if (options.table) {
      this.table(options.table);
    }
  };

  _Logger.prototype.group = function group (options, title) {
      var args = [], len = arguments.length - 2;
      while ( len-- > 0 ) args[ len ] = arguments[ len + 2 ];

    if (typeof options === 'string') {
      args = title ? [title ].concat( args) : args;
      title = options;
      options = {};
    }

    if (state.groupLevel === 0) {
      title = PREFIX + " " + title;
    }

    if (state.debug) {
      if (console && console.groupCollapsed && console.group) {
        var group = options.expanded
          ? console.group
          : console.groupCollapsed;

        state.groupLevel += 1;

        group.apply(void 0, [ title ].concat( args ));
      }
    }
  };

  _Logger.prototype.groupEnd = function groupEnd () {
    if (state.debug) {
      if (console && console.groupEnd) {
        state.groupLevel -= 1;

        console.groupEnd();
      }
    }
  };

  _Logger.prototype.info = function info (msg) {
    if (typeof console !== 'undefined' && console.info) {
      console.info(msg);
    }
  };

  _Logger.prototype.pause = function pause () {
    state.isPaused = true;
  };

  _Logger.prototype.resume = function resume () {
    state.isPaused = false;
  };

  prototypeAccessors.debugMode.set = function (debug) {
    this.debug(("Debug logging " + (debug ? 'enabled' : 'disabled')));

    state.debug = !!debug;

    setCookie({
      name: DEBUG_COOKIE,
      value: state.debug,
      expires: MAX_EXPIRY,
    });
  };

  Object.defineProperties( _Logger.prototype, prototypeAccessors );

  return new _Logger();
}

var logger = new Logger();

// message types are the functions that segment exposes
var MESSAGE_TYPES = ['track', 'page', 'identify', 'group'];

var ERRORS = {
  UNCATEGORIZED: 'uncategorized',
  VALIDATION: 'validation',
  LOCAL_STORAGE_FULL: 'localStorageFull',
};

function getErrorType(error) {
  var errorType = ERRORS.UNCATEGORIZED;

  if (error.message === 'Validation error') {
    errorType = ERRORS.VALIDATION;
  }

  // Known bug on Safari where segment client will try writing to localStorage without
  // checking if it's full first and causing the whole tracking / identify method to fail
  else if (error.name === 'QuotaExceededError') {
    errorType = ERRORS.LOCAL_STORAGE_FULL;
  }

  return errorType;
}

function getUtcOffset() {
  /**
   * getTimezoneOffset() returns the difference between UTC time and local time.
   * getTimezoneOffset() returns the difference in minutes.
   * For example, if your time zone is GMT+2, -120 will be returned.
   */
  var offsetInMins = new Date().getTimezoneOffset();
  var result = offsetInMins / -60;
  return result === 0 && 1 / result === -Infinity ? 0 : result;
}

function flattenObject(object, prefix) {
  if ( object === void 0 ) object = {};

  var flattenedObject = {};

  Object.keys(object).forEach(function (property) {
    var path = [prefix, property]
      .filter(function (value) { return value !== undefined; })
      .join('_');

    if (typeof object[property] === 'object') {
      flattenedObject = Object.assign({}, flattenedObject,
        flattenObject(object[property], path));
    } else {
      flattenedObject[path] = object[property];
    }
  });

  return flattenedObject;
}

function removeNull(obj) {
  if (obj === null) { return false; }
  if (typeof obj !== 'object') { return false; }

  Object.keys(obj).forEach(function (key) {
    if (obj[key] && typeof obj[key] === 'object') { removeNull(obj[key]); }
    else if (obj[key] == null) { delete obj[key]; }
  });

  return obj;
}

/*
converts event to have names
  {
    action: 'shown',
    event: { name: 'cme_quick_link' },
    product: 'uncategorized'
  }

  // to

  {
    action: { name: 'shown' },
    event: { name: 'cme_quick_link' },
    product: { name: 'uncategorized' }
  }
*/
function formatPayload(payload) {
  var SHORTHAND_PROPERTIES = ['event', 'product', 'action', 'view'];

  var newPayload = Object.assign({}, payload);

  SHORTHAND_PROPERTIES.forEach(function (property) {
    if (typeof payload[property] === 'string') {
      newPayload[property] = { name: payload[property] };
    }
  });

  return removeNull(newPayload);
}

var UTM_RE = new RegExp(CAMPAIGN_COOKIE);

function getCookie$1() {
  return document.cookie.split(' ').filter(function (c) { return c.match(UTM_RE); });
}
function getSearch() {
  return document.location.search;
}

var initialUTM = {
  cookie: getCookie$1(),
  query: getSearch()
};

// This function exists so that we can detect invalid UTM reports from the client
// when we have a valid source but an invalid uuid
// we only care about the first invalid event all other tracks from then out will either be valid or not valid
function applyReportBadUtmData($doxErrors) {
  if (!$doxErrors) { return function () {}; }

  return function reportBadUtmData(method, uuid, extra, track) {
    if (uuid !== 'uncategorized_campaign_identification_completed') { return; }
    if (track.context.campaign.medium !== UTM_MEDIUM_EMAIL) { return; }
    if (track.context.campaign.source !== UTM_SOURCE_DOXIMITY_EDA) { return; }
    if (track.context.campaign.uuid) { return; }

    var trackUtm = {
      cookie: getCookie$1(),
      query: getSearch(),
      method: method,
      campaign: track.context.campaign
    };

    var initialCookie = initialUTM.cookie.find(function (c) { return c.includes(CAMPAIGN_COOKIE); });
    var afterCookie = trackUtm.cookie.find(function (c) { return c.includes(CAMPAIGN_COOKIE); });
    var campaignCookieUnchanged =
      initialCookie && afterCookie && (initialCookie.includes(afterCookie) || afterCookie.includes(initialCookie));

    if (campaignCookieUnchanged) { return; }

    var InvalidUTMDataForEDA = new Error("New Cookie: Invalid UTM Data for EDA");
    $doxErrors.notify(InvalidUTMDataForEDA, {
      meta: {
        issue: 'utm-eda',
        initialUtm: initialUTM,
        trackUtm: trackUtm
      }
    });
  };
}

var UNKNOWN = 'UNKNOWN';

function noop$1() {}

// Integrated Browser Strategy
// This class name is no longer accurate, as now we no longer do client-side validation.
var SendStrategy = function SendStrategy(ref) {
  var doxAnalytics = ref.doxAnalytics;
  var logger = ref.logger;
  var onSend = ref.onSend;

  this.doxAnalytics = doxAnalytics;
  this.logger = logger;
  this.onSend = onSend;

  this.normalizeProperties = this.normalizeProperties.bind(this);
  this.normalizeValidation = this.normalizeValidation.bind(this);

  this.identify = this.identify.bind(this);
  this.group = this.group.bind(this);
  this.page = this.page.bind(this);
  this.track = this.track.bind(this);
};

SendStrategy.prototype.normalizeValidation = function normalizeValidation (properties) {
  var ref = this.doxAnalytics;
    var context = ref.context;
    var identification = ref.identification;

  var clonedContext = JSON.parse(JSON.stringify(context));

  // The way that we pass context through the system is to add it on the original properties object
  // we attempted to allow a new property to be added with calls but the number of changes to support that
  // is rather large and we are aiming to refactor this code to be easier to maintain in the future

  // to suppor this we add the properties context into the context object and delete
  // we are explicitly adding support for only validation here at the time
  // this was explicit decision because most context is static
  if (properties.context && properties.context.validation && properties.context.validation.minimum_schema_version) {
    clonedContext.validation = Object.assign({}, properties.context.validation);
  }

  delete properties.context;

  var validationPayload = Object.assign({}, {app: clonedContext.app,
    campaign: clonedContext.campaign,
    auth: clonedContext.auth,
    registrar: clonedContext.registrar,
    singular_device_id: clonedContext.singular_device_id},
    properties,
    identification);

  // Campaign is an optional property, so if it's null,
  // that should be ok, just omit it from the payload, otherwise
  // validation will fail it for not being defined (null) and not an object.
  if (validationPayload.campaign === null) {
    validationPayload.campaign = undefined;
  }

  var contextAndIdentification = Object.assign({}, {context: clonedContext},
    identification);

  return {
    validationPayload: validationPayload,
    contextAndIdentification: contextAndIdentification
  };
};

SendStrategy.prototype.normalizeProperties = function normalizeProperties (payload, options) {
    if ( options === void 0 ) options = {};

  var onValidation = options.onValidation; if ( onValidation === void 0 ) onValidation = noop$1;
    var onSend = options.onSend; if ( onSend === void 0 ) onSend = this.onSend;

  var formattedPayload = formatPayload(payload);
  var product = formattedPayload.product; if ( product === void 0 ) product = {};
    var event = formattedPayload.event; if ( event === void 0 ) event = {};
    var action = formattedPayload.action; if ( action === void 0 ) action = {};

  var properties = Object.assign({}, formattedPayload,
    this.doxAnalytics.common);

  return {
    onValidation: onValidation,
    onSend: onSend,
    product: product,
    event: event,
    action: action,
    properties: properties,
    formattedPayload: formattedPayload
  };
};

SendStrategy.prototype.track = function track (payload, options) {
    var ref$2, ref$3;

    if ( options === void 0 ) options = {};
  var ref = this.normalizeProperties(payload, options);
    var properties = ref.properties;
    var onValidation = ref.onValidation;
    var onSend = ref.onSend;
    var product = ref.product;
    var event = ref.event;
    var action = ref.action;

  var eventName =
    product.name && event.name && action.name ? [product.name, event.name, action.name].join('_') : UNKNOWN;
  this.logger.group(("track: " + eventName));
  this.logger.debug("track called with", payload);

  var ref$1 = this.normalizeValidation(properties);
    var validationPayload = ref$1.validationPayload;
    var contextAndIdentification = ref$1.contextAndIdentification;

  var argsAJS = [];
  argsAJS.push(eventName, flattenObject(properties), contextAndIdentification);

  (ref$2 = this.logger).debug.apply(ref$2, [ "AJS track() called with args" ].concat( argsAJS ));

  if (options.handleTrack) {
    options.handleTrack.apply(options, argsAJS.concat( [onValidation], ['dox-analytics'] ));
  } else {
    (ref$3 = this.doxAnalytics.external.analytics).track.apply(ref$3, argsAJS.concat( [onValidation], ['dox-analytics'] ));
  }
  onSend.apply(void 0, [ 'track' ].concat( argsAJS ));

  return {
    argsAJS: argsAJS,
    validationPayload: validationPayload
  };
};

SendStrategy.prototype.identify = function identify (payload, options) {
    var ref$2, ref$3;

    if ( options === void 0 ) options = {};
  var ref = this.normalizeProperties(payload, options);
    var properties = ref.properties;
    var onValidation = ref.onValidation;
    var onSend = ref.onSend;
    var formattedPayload = ref.formattedPayload;

  var eventName = formattedPayload.userId || this.doxAnalytics.anonymousId || UNKNOWN;
  this.logger.group(("identify: " + eventName));
  this.logger.debug("identify called with", payload);

  if (properties.userId) {
    this.doxAnalytics.userId = properties.userId;
  }

  if (properties.authApiUserUuid) {
    this.doxAnalytics.authApiUserUuid = properties.authApiUserUuid;
  }

  if (properties.registrarSource) {
    this.doxAnalytics.registrarSource = properties.registrarSource;
  }

  if (properties.traits) {
    this.doxAnalytics.traits = properties.traits;
  } else {
    properties.traits = this.doxAnalytics.traits;
  }

  var ref$1 = this.normalizeValidation(properties);
    var validationPayload = ref$1.validationPayload;
    var contextAndIdentification = ref$1.contextAndIdentification;

  var argsAJS = [];
  argsAJS.push(eventName, flattenObject(properties.traits), contextAndIdentification);
  (ref$2 = this.logger).debug.apply(ref$2, [ "AJS identify() called with args" ].concat( argsAJS ));
  (ref$3 = this.doxAnalytics.external.analytics).identify.apply(ref$3, argsAJS.concat( [onValidation], ['dox-analytics'] ));
  onSend.apply(void 0, [ 'identify' ].concat( argsAJS ));

  return {
    argsAJS: argsAJS,
    validationPayload: validationPayload
  };
};

SendStrategy.prototype.page = function page (payload, options) {
    var ref$2, ref$3;

  var ref = this.normalizeProperties(payload, options);
    var properties = ref.properties;
    var onValidation = ref.onValidation;
    var onSend = ref.onSend;
    var formattedPayload = ref.formattedPayload;

  var eventName =
    formattedPayload.product.name && formattedPayload.view.name
      ? [formattedPayload.product.name, formattedPayload.view.name].join('_')
      : UNKNOWN;

  this.logger.group(("page: " + eventName));
  this.logger.debug("page called with", payload);

  if (properties.timezone !== 'number') {
    properties.timezone = getUtcOffset();
  }

  var ref$1 = this.normalizeValidation(properties);
    var validationPayload = ref$1.validationPayload;
    var contextAndIdentification = ref$1.contextAndIdentification;

  var argsAJS = [];

  argsAJS.push(eventName, flattenObject(properties), contextAndIdentification);

  (ref$2 = this.logger).debug.apply(ref$2, [ "AJS page() called with args" ].concat( argsAJS ));
  (ref$3 = this.doxAnalytics.external.analytics).page.apply(ref$3, argsAJS.concat( [onValidation], ['dox-analytics'] ));
  onSend.apply(void 0, [ 'page' ].concat( argsAJS ));

  return {
    argsAJS: argsAJS,
    validationPayload: validationPayload
  };
};

SendStrategy.prototype.group = function group (payload, options) {
    var ref$2, ref$3;

    if ( options === void 0 ) options = {};
  var ref = this.normalizeProperties(payload, options);
    var properties = ref.properties;
    var onValidation = ref.onValidation;
    var onSend = ref.onSend;
  var eventName = properties.groupId;
  this.logger.group(("group: " + eventName));
  this.logger.debug("group called with", payload);

  var ref$1 = this.normalizeValidation(properties);
    var validationPayload = ref$1.validationPayload;

  var argsAJS = [];
  argsAJS.push(eventName, flattenObject(properties.traits));

  (ref$2 = this.logger).debug.apply(ref$2, [ "AJS group() called with args" ].concat( argsAJS ));
  (ref$3 = this.doxAnalytics.external.analytics).group.apply(ref$3, argsAJS.concat( [onValidation], ['dox-analytics'] ));
  onSend.apply(void 0, [ 'group' ].concat( argsAJS ));

  return {
    argsAJS: argsAJS,
    validationPayload: validationPayload
  };
};

/*
  Consolidates the ways that we can provide and hook into dox-analytics lifecycles
   - Handles pulling defaults from HTML, meta, window and query params
   - Exposes hook methods that can be provided from the document

  Convention: Provide a meta name that begins with dox-{KEY}
  <meta name="dox-analytics-api-key" content="my_segment_key">
  <meta name="dox-maestro-app-version" content="hash">
  documentHelper.apiKey()
*/

var DocumentHelper = function DocumentHelper(external) {
  this.external = external || window;
};

DocumentHelper.prototype.getMeta = function getMeta (key) {
  var meta = this.external.document.querySelector(("meta[name=" + key + "]"));

  if (meta) {
    return meta.content;
  }

  return undefined;
};

DocumentHelper.prototype.analyticsUrl = function analyticsUrl () {
  return this.getMeta('dox-analytics-track-url');
};

DocumentHelper.prototype.apiKey = function apiKey () {
  return this.getMeta('dox-analytics-api-key');
};

DocumentHelper.prototype.appRelease = function appRelease () {
  return this.getMeta('dox-maestro-app-version');
};

DocumentHelper.prototype.authState = function authState () {
  return this.getMeta('dox-analytics-auth-state');
};

DocumentHelper.prototype.singularSdkKey = function singularSdkKey () {
  return this.getMeta('dox-analytics-singular-sdk-key');
};

DocumentHelper.prototype.singularSdkSecret = function singularSdkSecret () {
  return this.getMeta('dox-analytics-singular-sdk-secret');
};

DocumentHelper.prototype.singularSdkProductId = function singularSdkProductId () {
  return this.getMeta('dox-analytics-singular-sdk-product-id');
};

DocumentHelper.prototype.href = function href () {
  return this.external.document.location.href;
};

function noop$2() {}

/*
  API

  // Use Defaults
  // basic controls
  new AnalyticsLoader({
    apiKey, // Key used to identify the segment hosted script
    onLoadError(e) { send this somewhere },
    onLoad() { do something },
  })

  // advanced use cases


  // You can pass analytics values that will be proxied directly to the associated values for
  // the analytics library
  new AnalyticsLoader({
    apiKey: ...,

    // setAnalyticsGlobalValues are set on the analytics global value
    setAnalyticsGlobalValues: {...},
    snippetVersion: '1243...'
    analyticsLoadUrls: [... array of urls to load from],
    scriptAttrs: {...} // attributes to attach to the script as it is loaded
  })

*/

var PROD_URL = 'doximity.com';
var PRE_URL = 'dogsimity.com';

var AnalyticsLoader = function AnalyticsLoader(options) {
  if ( options === void 0 ) options = {};

  this.load = this.load.bind(this);
  this.external = options.external || window;
  this.documentHelper = new DocumentHelper(this.external);

  this.apiKey = options.apiKey || options.segmentKey || this.documentHelper.apiKey();

  if (!this.apiKey) {
    var error = new Error('You must pass a apiKey to AnalyticsLoader or set meta tag dox-analytics-api-key');
    throw error;
  }

  this.apiHost = options.analyticsUrl || this.documentHelper.analyticsUrl();
  this.apiHostProtocol = 'https';

  var href = this.documentHelper.href();

  if (!this.apiHost) {
    if (href.includes(PROD_URL)) {
      this.apiHost = "https://ta." + PROD_URL;
    } else if (href.includes(PRE_URL)) {
      this.apiHost = "https://ta." + PRE_URL;
    } else {
      this.apiHost = 'ta.doximity.com';
    }
  }

  if (this.apiHost.includes('http://')) {
    this.apiHost = this.apiHost.replace('http://', '');
    this.apiHostProtocol = 'http';
  }

  if (this.apiHost.includes('https://')) {
    this.apiHost = this.apiHost.replace('https://', '');
    this.apiHostProtocol = 'https';
  }

  if (!this.apiHost.endsWith('/v1')) {
    this.apiHost = (this.apiHost) + "/v1";
  }

  this.onLoad = options.onLoad || noop$2;
};

AnalyticsLoader.load = function load (options) {
    if ( options === void 0 ) options = {};

  var loader = new AnalyticsLoader(options);

  loader.load();

  return loader;
};

AnalyticsLoader.prototype.browserSettings = function browserSettings () {
  var settings = {
    apiKey: this.apiKey,
    cdnSettings: {
      integrations: {
        'Mixpanel (Actions)': {
          versionSettings: {
            componentTypes: []
          }
        },
        'Segment.io': {
          apiKey: this.apiKey,
          apiHost: this.apiHost,
          protocol: this.apiHostProtocol,
          unbundledIntegrations: ['Mixpanel'],
          addBundledMetadata: true,
          maybeBundledConfigIds: {},
          versionSettings: {
            version: '4.13.1',
            componentTypes: ['browser']
          },
          deliveryStrategy: {
            strategy: 'batching',
            config: {
              size: 16,
              timeout: 300
            }
          }
        }
      }
    }
  };

  if (this.apiHostProtocol === 'http') {
    settings.cdnSettings.integrations['Segment.io'].deliveryStrategy = {
      strategy: 'standard',
      config: { keepalive: true }
    };
  }

  return settings;
};

AnalyticsLoader.prototype.load = function load () {
  var browserSettings = this.browserSettings();

  this.analytics = AnalyticsBrowser.load(browserSettings);
  this.external.analytics = this.analytics;
};

// Singular SDK is a UMD bundle that exposes global variables

var SingularManager = function SingularManager(options) {
  if ( options === void 0 ) options = {};

  this.sdkKey = options.sdkKey;
  this.sdkSecret = options.sdkSecret;
  this.productId = options.productId;
  this.userId = options.userId || null;
  this.logger = options.logger || console;
  this.initialized = false;
};

SingularManager.prototype.init = function init () {
  if (!this.isConfigValid()) {
    this.logger.debug('Singular SDK is missing required parameters and will not be initialized');
    return false;
  }

  try {
    // Access Singular from window after import
    var singularSdk = window.singularSdk;
      var SingularConfig = window.SingularConfig;

    if (!singularSdk || !SingularConfig) {
      this.logger.debug('Singular SDK not loaded properly');
      return false;
    }

    // Build config with chained methods
    var config = new SingularConfig(this.sdkKey, this.sdkSecret, this.productId);

    // If user ID is known at initialization, set it in config
    if (this.userId) {
      config = config.withCustomUserId(this.userId);
    }

    // Initialize the SDK
    singularSdk.init(config);
    this.initialized = true;
    this.logger.debug('Singular SDK initialized successfully');
    return true;
  } catch (error) {
    this.logger.debug('Failed to initialize Singular SDK', error);
    return false;
  }
};

SingularManager.prototype.isConfigValid = function isConfigValid () {
  return !!(this.sdkKey && this.sdkSecret && this.productId);
};

SingularManager.prototype.login = function login (userId) {
  if (!this.initialized) {
    this.logger.debug('Singular SDK not initialized - cannot login user');
    return;
  }

  if (userId && window.singularSdk) {
    try {
      window.singularSdk.login(userId);
      this.userId = userId;
      this.logger.debug('Singular SDK user logged in', userId);
    } catch (error) {
      this.logger.debug('Failed to login user to Singular SDK', error);
    }
  }
};

SingularManager.prototype.logout = function logout () {
  if (!this.initialized) {
    this.logger.debug('Singular SDK not initialized - cannot logout user');
    return;
  }

  if (window.singularSdk) {
    try {
      window.singularSdk.logout();
      this.userId = null;
      this.logger.debug('Singular SDK user logged out');
    } catch (error) {
      this.logger.debug('Failed to logout user from Singular SDK', error);
    }
  }
};

SingularManager.prototype.getSingularDeviceId = function getSingularDeviceId () {
  if (!this.initialized) {
    this.logger.debug('Singular SDK not initialized - cannot get device ID');
    return undefined;
  }

  if (window.singularSdk) {
    try {
      return window.singularSdk.getSingularDeviceId();
    } catch (error) {
      this.logger.debug('Failed to get Singular device ID', error);
      return undefined;
    }
  }

  return undefined;
};

/* global "5.13.0" */
function objectWithoutProperties (obj, exclude) { var target = {}; for (var k in obj) if (Object.prototype.hasOwnProperty.call(obj, k) && exclude.indexOf(k) === -1) target[k] = obj[k]; return target; }

var NO_RELEASE_SPECIFIED = 'NO_RELEASE_SPECIFIED';

var AUTH_STATES = ['unspecified', 'none', 'inferred', 'identified', 'authenticated', 'superauthenticated'];

var noop$3 = function () {};

/*
  This additional state is here because we are calling the init function 2-3 times
  every time we start a dox analytics instance
  Although this is a bug, we are just working around this for now
  - One time in main
  - One time when we call new in the application
  - One time when we call init() in the application
*/
var hasInitialized = false;
function DoxAnalytics(ref) {
  if ( ref === void 0 ) ref = {};
  var external = ref.external; if ( external === void 0 ) external = window;
  var rest = objectWithoutProperties( ref, ["external"] );
  var options = rest;

  if (typeof external !== 'undefined') {
    if (!external.analytics) {
      AnalyticsLoader.load(Object.assign({}, options,
        {external: external}));
    }
  }

  // Put items that should never be directly accessed in state closure
  // Examples: viewId since those should be accessed through getter
  var state = {
    sections: [],
    shownRegistry: {},
    traits: {},
    viewId: undefined,
    getUserId: noop$3,
    getAuthApiUserUuid: noop$3,
    getRegistrarSource: noop$3
  };

  // eslint-disable-next-line no-console
  if (!external.analytics && console && console.warn) {
    var errMsg = 'Segment.io analytics.js not detected on this page.';
    console.warn(errMsg); // eslint-disable-line no-console
    if (options.errorHandler) {
      options.errorHandler(new Error(errMsg), {});
    }
  }

  function refreshSectionIds() {
    state.sections.forEach(function (section) { return section.refreshId(); });
  }

  function refreshViewId(viewId) {
    if ( viewId === void 0 ) viewId = generateUUID();

    state.viewId = viewId;
    refreshSectionIds();
  }

  // Loop checking for shown events
  function checkShownLoop() {
    Object.keys(state.shownRegistry).forEach(function (impression) {
      checkShown(state.shownRegistry[impression]);
    });
  }

  var TWELVE_FPS = 1000 / 12;
  setInterval(checkShownLoop, TWELVE_FPS);

  /*
    Options can be a function that is an onValidation function, an object or undefined
  */
  function normalizeOptions(arg) {
    if (typeof arg === 'function') {
      return {
        onValidation: arg
      };
    }

    if (!arg) {
      return {
        onValidation: noop$3
      };
    }

    return Object.assign({}, {onValidation: noop$3},
      arg);
  }

  var _DoxAnalytics = function _DoxAnalytics() {
    this.version = "5.13.0";
    this.refreshViewId = refreshViewId;

    if (options && options.application) {
      this.init(options);
    }
  };

  var prototypeAccessors = { userId: { configurable: true },authApiUserUuid: { configurable: true },registrarSource: { configurable: true },traits: { configurable: true },sections: { configurable: true },viewId: { configurable: true },anonymousId: { configurable: true },identification: { configurable: true },registrar: { configurable: true },singularDeviceId: { configurable: true },common: { configurable: true },context: { configurable: true },app: { configurable: true },campaign: { configurable: true },debug: { configurable: true } };

  _DoxAnalytics.prototype.init = function init (initOptions) {
      var this$1 = this;
      if ( initOptions === void 0 ) initOptions = {};

    var application = initOptions.application;
      var release = initOptions.release;
      var platform = initOptions.platform;
      var viewId = initOptions.viewId;
      var errorHandler = initOptions.errorHandler; if ( errorHandler === void 0 ) errorHandler = noop$3;
      var directReferrerDomain = initOptions.directReferrerDomain;
      var shouldTrackInitialCampaign = initOptions.shouldTrackInitialCampaign; if ( shouldTrackInitialCampaign === void 0 ) shouldTrackInitialCampaign = true;
      var collectUserIp = initOptions.collectUserIp; if ( collectUserIp === void 0 ) collectUserIp = true;
      var $doxErrors = initOptions.$doxErrors; if ( $doxErrors === void 0 ) $doxErrors = null;
      var TrackingStrategyConstructor = initOptions.TrackingStrategyConstructor; if ( TrackingStrategyConstructor === void 0 ) TrackingStrategyConstructor = SendStrategy;
    var onSend = initOptions.onSend || applyReportBadUtmData($doxErrors);

    this.$doxErrors = $doxErrors;
    this.viewId = viewId;
    this.application = application;

    this.authContext = null;

    var documentHelper = new DocumentHelper(external);

    // Extract release from meta tag convention
    var getReleaseFromPage = function () { return documentHelper.appRelease(); };
    // cast release value to a string or a default value
    this.release = String(release || getReleaseFromPage() || NO_RELEASE_SPECIFIED);
    this.platform = platform;
    this.directReferrerDomain = directReferrerDomain;
    this.initialized = true;
    this.shouldTrackInitialCampaign = shouldTrackInitialCampaign;
    this.collectUserIp = collectUserIp;

    var singularSdkKey = initOptions.singularSdkKey || documentHelper.singularSdkKey();
    var singularSdkSecret = initOptions.singularSdkSecret || documentHelper.singularSdkSecret();
    var singularSdkProductId =
      initOptions.singularSdkProductId || documentHelper.singularSdkProductId() || 'com.doximity';

    // We need SDK key and secret to initialize Singular SDK (product ID has a default)
    if (singularSdkKey && singularSdkSecret) {
      this.singularManager = new SingularManager({
        sdkKey: singularSdkKey,
        sdkSecret: singularSdkSecret,
        productId: singularSdkProductId,
        userId: this.userId,
        logger: logger
      });

      if (this.singularManager.init()) {
        logger.debug('Singular SDK integrated and initialized');
      }
    } else {
      logger.debug('Singular SDK not initialized - missing configuration');
    }

    var initiallyDetectedCampaign = detectInitialBrowserCampaign();

    setActiveCampaign();

    this.external = external;

    var strategy = new TrackingStrategyConstructor({
      doxAnalytics: this,
      logger: logger,
      $doxErrors: $doxErrors,
      external: external,
      onSend: onSend
    });

    MESSAGE_TYPES.forEach(function (type) {
      // NOTE: Was getting weird errors when splatting this to and from arguments so opted for this direct arguments option
      this$1[type] = function (payload, _options) {
          if ( _options === void 0 ) _options = {};

        try {
          strategy[type](payload, normalizeOptions(_options));
          logger.groupEnd();
        } catch (error) {
          logger.groupEnd();
          var errorType = getErrorType(error);

          errorHandler(error, {
            version: this$1.version,
            type: type,
            errorType: errorType,
            payload: payload,
            options: options,
            validationErrors: error.validationErrors
          });

          if (_options.onValidation) {
            _options.onValidation(error);
          }
        }

        return this$1;
      };
    });

    if (!hasInitialized) {
      if (this.shouldTrackInitialCampaign) {
        trackCampaignInitiated(this, initiallyDetectedCampaign);
      }

      external.analytics.reset();

      hasInitialized = true;
    }
  };

  prototypeAccessors.userId.set = function (userIdGetter) {
    if (typeof userIdGetter !== 'function') {
      var userId$1 = userIdGetter;
      userIdGetter = function () { return userId$1; };
    }

    // Set userId to `undefined` if false
    state.getUserId = userIdGetter || noop$3;

    var userId = userIdGetter();

    // Sync with Singular SDK
    if (this.singularManager) {
      if (userId) {
        this.singularManager.login(userId);
      } else {
        this.singularManager.logout();
      }
    }

    // Clear traits when userId is cleared
    if (!userId) {
      state.traits = {};

      external.analytics.ready(function () { return external.analytics.reset(); });

      logger.debug('User ID and traits cleared');
    } else {
      logger.debug('Set User ID', userId);
    }
  };

  prototypeAccessors.userId.get = function () {
    var userId = state.getUserId();

    logger.debug('userId', userId || 'not set');

    // Return undefined if userId is falsey
    return userId || undefined;
  };

  prototypeAccessors.authApiUserUuid.set = function (authApiUserUuidGetter) {
    if (typeof authApiUserUuidGetter !== 'function') {
      var authApiUserUuid = authApiUserUuidGetter;
      authApiUserUuidGetter = function () { return authApiUserUuid; };
    }

    // Set userId to `undefined` if false
    state.getAuthApiUserUuid = authApiUserUuidGetter || noop$3;
  };

  prototypeAccessors.authApiUserUuid.get = function () {
    var authApiUserUuid = state.getAuthApiUserUuid();

    logger.debug('authApiUserUuid', authApiUserUuid || 'not set');

    // Return undefined if authApiUserUuid is falsey
    return authApiUserUuid || undefined;
  };

  prototypeAccessors.registrarSource.set = function (registrarSourceGetter) {
    if (typeof registrarSourceGetter !== 'function') {
      var registrarSource = registrarSourceGetter;
      registrarSourceGetter = function () { return registrarSource; };
    }

    // Set userId to `undefined` if false
    state.getRegistrarSource = registrarSourceGetter || noop$3;
  };

  prototypeAccessors.registrarSource.get = function () {
    var registrarSource = state.getRegistrarSource();

    logger.debug('registrarSource', registrarSource || 'not set');

    // Return clinician if registrarSource is falsey
    return registrarSource || 'clinician';
  };

  prototypeAccessors.traits.set = function (traits) {
    // Merge new traits with existing
    state.traits = Object.assign({}, state.traits,
      traits);
  };

  prototypeAccessors.traits.get = function () {
    var traits = state.traits;

    logger.debug('traits', traits);

    return traits;
  };

  _DoxAnalytics.prototype.registerSection = function registerSection (sectionOptions) {
      if ( sectionOptions === void 0 ) sectionOptions = {};

    var section = new Section(Object.assign({}, {onTrack: this.track,
      shownRegistry: state.shownRegistry},
      sectionOptions));
    state.sections.push(section);

    logger.debug('Registered section', section);

    return section;
  };

  _DoxAnalytics.prototype.unregisterSection = function unregisterSection (sectionId, ref) {
      if ( ref === void 0 ) ref = {};
      var delay = ref.delay;

    var cleanupFn = function () {
      state.sections = state.sections.filter(function (section) { return section.id !== sectionId; });

      logger.debug('Unregistered section with id', sectionId);
    };

    if (delay) {
      setTimeout(cleanupFn, delay);
    } else {
      cleanupFn();
    }
  };

  prototypeAccessors.sections.get = function () {
    logger.debug('sections', state.sections);

    return state.sections;
  };

  prototypeAccessors.viewId.get = function () {
    logger.debug('viewId', state.viewId);

    return state.viewId;
  };

  prototypeAccessors.viewId.set = function (viewId) {
    refreshViewId(viewId);
  };

  prototypeAccessors.anonymousId.get = function () {
    var anonymousId = setAnonymousIdCookie();

    logger.debug('anonymousId', anonymousId);

    return anonymousId;
  };

  prototypeAccessors.identification.get = function () {
    var identification = {
      anonymousId: this.anonymousId,
      userId: this.userId
    };

    logger.debug('identification', identification);

    return identification;
  };

  prototypeAccessors.registrar.get = function () {
    var registrar = {
      registrarIdentifier: this.userId,
      authApiUserUuid: this.authApiUserUuid,
      registrarSource: this.registrarSource
    };

    logger.debug('registrar', registrar);

    return registrar;
  };

  // Singular.net Web SDK integration - native or via GTM fallback
  // https://support.singular.net/hc/en-us/articles/360045803392-How-to-Verify-Device-Attribution
  prototypeAccessors.singularDeviceId.get = function () {
    try {
      // 1: Use native integration if available
      if (this.singularManager) {
        var deviceId = this.singularManager.getSingularDeviceId();
        if (deviceId) {
          logger.debug('singularDeviceId from integrated SDK', deviceId);
          return deviceId;
        }
      }

      // 2: Fall back to GTM integration during transition
      if (external && external.singularSdk && typeof external.singularSdk.getSingularDeviceId === 'function') {
        var id = external.singularSdk.getSingularDeviceId();
        logger.debug('singularDeviceId from GTM', id);
        return id || undefined;
      }
    } catch (error) {
      logger.debug('Failed to get Singular Device ID', error);
    }

    return undefined;
  };

  prototypeAccessors.common.get = function () {
    var common = {
      view_id: this.viewId
    };

    logger.debug('common', common);

    return common;
  };

  _DoxAnalytics.prototype.setAuthContext = function setAuthContext (state) {
    if (typeof state === 'function') {
      this.authContext = state;
    } else if (typeof state === 'object') {
      this.authContext = this.authContext || {};
      this.authContext = Object.assign({}, this.authContext,
        state);
    }
  };

  _DoxAnalytics.prototype.getAuthContext = function getAuthContext () {
    var auth;
    if (typeof this.authContext === 'function') {
      auth = this.authContext();
    } else if (typeof this.authContext === 'object') {
      auth = Object.assign({}, this.authContext);
    }

    var authKeysNotDefined = auth && !Object.keys(auth).length;
    // Fallback to metatags
    if (authKeysNotDefined) {
      var state = new DocumentHelper(external).authState();

      auth = { state: state };
    }

    if (!AUTH_STATES.includes(auth.state)) {
      auth.state = 'unspecified';
    }

    return auth;
  };

  prototypeAccessors.context.get = function () {
    var window_screen = window.screen; if ( window_screen === void 0 ) window_screen = {};
      var window_screen$1 = window_screen;
      var height = window_screen$1.height;
      var width = window_screen$1.width;
      var density = window.devicePixelRatio;

    var context = {
      app: this.app,
      auth: this.getAuthContext(),
      registrar: this.registrar,
      campaign: this.campaign,
      singular_device_id: this.singularDeviceId,
      library: {
        name: 'analytics.js',
        version: external.analytics.VERSION,
        doxAnalytics: {
          version: "5.13.0"
        }
      },
      screen: { height: height, width: width, density: density },
      hash: document.location.hash,
      search: document.location.search,
      title: document.title,
      url: document.location.href.includes('#')
        ? document.location.href.slice(0, document.location.href.indexOf('#'))
        : document.location.href,
      userAgent: window.navigator.userAgent
    };

    // See [Anonymizing IP][anonymizing-ip]
    //
    // [anonymizing-ip]: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/identity/#anonymizing-ip
    if (!this.collectUserIp) { context.ip = '0.0.0.0'; }

    logger.debug('context', context);

    return context;
  };

  prototypeAccessors.app.get = function () {
    if (!this.application) { throw new Error("Analytics property 'application' has not been set."); }
    if (!this.release) { throw new Error("Analytics property 'release' has not been set."); }

    var app = {
      name: this.application,
      platform: this.platform || getPlatform(),
      release: this.release
    };

    logger.debug('app', app);

    return app;
  };

  prototypeAccessors.campaign.get = function () {
    var campaign = getCampaign(this.directReferrerDomain);

    logger.debug('campaign', campaign);

    return campaign;
  };

  prototypeAccessors.debug.set = function (debug) {
    logger.debugMode = debug;
  };

  Object.defineProperties( _DoxAnalytics.prototype, prototypeAccessors );

  return new _DoxAnalytics(options);
}

export default DoxAnalytics;
export { checkShown };
//# sourceMappingURL=dox-analytics.esm.js.map
